import { z } from 'zod';

/**
 * Zod schemas for workflow-related entities
 * These provide runtime validation and type inference
 */

// Workflow enums (coerce from string for API/Prisma)
const workflowTriggerValues = [
  'SCHEDULE_MONTHLY', 'SCHEDULE_DAILY', 'EVENT_NEW_TENANT', 'EVENT_TICKET_STALLED', 'EVENT_RENT_OVERDUE',
  'EVENT_LEASE_EXPIRING', 'EVENT_LEASE_CREATED', 'EVENT_PAYMENT_RECEIVED', 'EVENT_TICKET_CREATED',
  'EVENT_APPLICATION_SUBMITTED', 'EVENT_APPLICATION_APPROVED', 'EVENT_APPLICATION_DENIED',
  'EVENT_APPLICATION_STATUS_CHANGED', 'EVENT_COMPLIANCE_DOCUMENT_EXPIRED',
] as const;
const workflowActionValues = ['CHARGE_FEE', 'SEND_EMAIL', 'SEND_SMS', 'CREATE_TASK', 'UPDATE_TICKET_PRIORITY', 'CREATE_TICKET'] as const;
const workflowStatusValues = ['Active', 'Paused'] as const;

const workflowTriggerCoerce = z.string().transform(s => (workflowTriggerValues.includes(s as (typeof workflowTriggerValues)[number]) ? s as (typeof workflowTriggerValues)[number] : 'SCHEDULE_DAILY'));
const workflowActionCoerce = z.string().transform(s => (workflowActionValues.includes(s as (typeof workflowActionValues)[number]) ? s as (typeof workflowActionValues)[number] : 'SEND_EMAIL'));
const workflowStatusCoerce = z.string().transform(s => (workflowStatusValues.includes(s as (typeof workflowStatusValues)[number]) ? s as (typeof workflowStatusValues)[number] : 'Active'));

// Workflow schema (API/Prisma: coerce trigger, action, status)
export const workflowSchema = z.object({
  id: z.string(),
  title: z.string().transform(t => (t?.trim() || 'Workflow')),
  description: z.string().default(''),
  trigger: workflowTriggerCoerce,
  triggerConfig: z.object({
    day: z.number().int().nullish(),
    thresholdDays: z.number().int().nonnegative().nullish(),
    minScore: z.number().nullish(),
    maxScore: z.number().nullish(),
    source: z.string().nullish(),
    hasCoSigner: z.boolean().nullish(),
    hasPets: z.boolean().nullish(),
    minCreditScore: z.number().nullish(),
    minIncome: z.number().nullish(),
  }).passthrough().nullish(),
  action: workflowActionCoerce,
  actionConfig: z.object({
    amount: z.number().nullish(),
    templateId: z.string().nullish(),
    targetPriority: z.string().nullish(),
  }).passthrough().nullish(),
  status: workflowStatusCoerce,
  lastRun: z.union([z.string(), z.date()]).nullish().transform((v) => (v instanceof Date ? v.toISOString() : v ?? undefined)),
  executionCount: z.number().int().nonnegative().nullish().default(0),
  icon: z.string().nullish(),
}).passthrough();

export type Workflow = z.infer<typeof workflowSchema>;

// WorkflowExecutionLog schema
export const workflowExecutionLogSchema = z.object({
  id: z.string(),
  workflowId: z.string(),
  workflowTitle: z.string(),
  timestamp: z.string(),
  status: z.enum(['Success', 'Failed', 'Skipped'] as const),
  details: z.string(),
});

export type WorkflowExecutionLog = z.infer<typeof workflowExecutionLogSchema>;
